import { ConditionalLink } from "components/conditionalLink";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { LinkBankAccount } from "components/LinkBankAccount";
import { Step } from "components/SVBConnectModal/SVBConnectModal";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useConfirmAccountsMutation } from "store/apis/bankConnections";
import { BankConnection } from "types/Models/banks";
import { BackendError } from "types/utils/error";

export const PlaidManageAccounts = ({
  connection,
  isOpen,
  close,
}: {
  connection: BankConnection;
  isOpen: boolean;
  close: () => void;
}) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId, secondary_channel_url } = useCurrentGroupContext();

  const [confirmAccounts, { isLoading: isConfirmingMigration }] =
    useConfirmAccountsMutation();

  const { successToast, alertToast } = useToast();

  const onNonsyncComplete = async (publicToken: string) => {
    try {
      await confirmAccounts({
        entityId: entityId!,
        payload: {
          mapping_type: "PLAID",
          public_token: publicToken,
          entity_item_id: connection.uuid,
        },
      }).unwrap();
      close();

      successToast({
        message: "Bank account connected successfully.",
      });
    } catch (error) {
      alertToast({
        message:
          (error as BackendError).data?.error?.message ||
          "An error occurred while connecting the bank account.",
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="large">
        <Modal.Header>
          <Modal.Title>Manage accounts</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <div className="t-space-y-1">
            <p className="t-text-body-sm t-text-text-60 t-mb-4">
              To manage the accounts, reconnect your {connection.name} via Plaid
              by following these simple steps:
            </p>
            <div className="t-ml-2">
              <Step step={1}>
                Click on “Update in Plaid” below and log in to your bank account
                via Plaid
              </Step>
              <Step step={2}>
                Select/Unselect the accounts you wish to connect with Inkle
              </Step>

              <Step step={3}>Confirm the changes made on Plaid</Step>
              <Step step={4} isLast>
                Navigate back to Inkle to see your connected accounts
              </Step>
            </div>
            <Banner
              type="warning"
              description="Unselecting only stops new data from coming in and the already synced data will have to be managed manually."
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="t-flex t-justify-between">
            <div className="t-flex t-gap-1 t-text-subtext t-text-text-30 t-items-center">
              <span>Need help?</span>
              <ConditionalLink
                className="t-text-purple"
                to={`/books/chat/${secondary_channel_url}`}
                target="_blank"
              >
                Talk to us
              </ConditionalLink>
            </div>
            <div className="t-flex t-gap-3">
              <Modal.RawClose asChild>
                <Button>Cancel</Button>
              </Modal.RawClose>

              <LinkBankAccount
                enableEntitySelection={false}
                sync={false}
                entityId={entityId}
                onNonSyncComplete={onNonsyncComplete}
                groupId={groupId}
              >
                {({ ready, connectPlaid }) => (
                  <Button
                    customType="primary"
                    onClick={connectPlaid}
                    isLoading={isConfirmingMigration}
                    disabled={!ready || isConfirmingMigration}
                  >
                    {isConfirmingMigration ? "Updating" : "Update in Plaid"}
                  </Button>
                )}
              </LinkBankAccount>
            </div>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
